import React, { useState, useEffect, useRef } from "react";
import {
  TravelExpense,
  ExpenseCategory,
  ExpenseFormData,
} from "../types/expense";
import EditExpenseModal from "./EditExpenseModal";

interface ExpenseListProps {
  expenses: TravelExpense[];
  onDeleteExpense: (id: string) => Promise<void>;
  onUpdateExpense: (id: string, updatedData: ExpenseFormData) => Promise<void>;
  showCategoryFilter?: boolean;
  summaryOnly?: boolean;
}

const ExpenseList: React.FC<ExpenseListProps> = ({
  expenses,
  onDeleteExpense,
  onUpdateExpense,
  showCategoryFilter = true,
  summaryOnly = false,
}) => {
  const [editingExpense, setEditingExpense] = useState<TravelExpense | null>(
    null,
  );
  const scrollContainerRefs = useRef<(HTMLDivElement | null)[]>([]);

  const setScrollContainerRef =
    (index: number) => (el: HTMLDivElement | null) => {
      scrollContainerRefs.current[index] = el;
    };

  const updateScrollShadows = (container: HTMLDivElement) => {
    const canScrollLeft = container.scrollLeft > 0;
    const canScrollRight =
      container.scrollLeft < container.scrollWidth - container.clientWidth;

    container.classList.toggle("can-scroll-left", canScrollLeft);
    container.classList.toggle("can-scroll-right", canScrollRight);
  };

  useEffect(() => {
    const containers = scrollContainerRefs.current.filter(Boolean);
    const cleanupFunctions: (() => void)[] = [];

    containers.forEach((container) => {
      if (container) {
        // Initial check
        updateScrollShadows(container);

        // Add scroll listener
        const handleScroll = () => updateScrollShadows(container);
        container.addEventListener("scroll", handleScroll);

        // Add resize listener
        const handleResize = () => updateScrollShadows(container);
        window.addEventListener("resize", handleResize);

        cleanupFunctions.push(() => {
          container.removeEventListener("scroll", handleScroll);
          window.removeEventListener("resize", handleResize);
        });
      }
    });

    return () => {
      cleanupFunctions.forEach((cleanup) => cleanup());
    };
  }, [expenses]);

  const formatCurrency = (amount: number) => {
    return (
      new Intl.NumberFormat("de-DE", {
        style: "currency",
        currency: "EUR",
      })
        .format(amount)
        .replace(/€/, "")
        .trim() + " €"
    );
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  // Split expenses by category only if showing category filter
  const belegeExpenses = showCategoryFilter
    ? expenses.filter((expense) => expense.category === ExpenseCategory.BELEGE)
    : expenses.filter((expense) => expense.category === ExpenseCategory.BELEGE);
  const spesenExpenses = showCategoryFilter
    ? expenses.filter(
        (expense) => expense.category === ExpenseCategory.SPESENABRECHNUNG,
      )
    : expenses.filter(
        (expense) => expense.category === ExpenseCategory.SPESENABRECHNUNG,
      );

  const getTotalAmount = (expenseList: TravelExpense[]) => {
    return expenseList.reduce((total, expense) => total + expense.amount, 0);
  };

  const handleEditExpense = (expense: TravelExpense) => {
    setEditingExpense(expense);
  };

  const handleCloseEditModal = () => {
    setEditingExpense(null);
  };

  const handleSaveExpense = async (id: string, updatedData: ExpenseFormData) => {
    try {
      await onUpdateExpense(id, updatedData);
      setEditingExpense(null);
    } catch (error) {
      console.error("Failed to update expense:", error);
      // Error is already handled in the parent component
    }
  };

  const handleDeleteExpense = async (id: string) => {
    try {
      await onDeleteExpense(id);
    } catch (error) {
      console.error("Failed to delete expense:", error);
    }
  };

  // If summaryOnly, show only the grand total
  if (summaryOnly) {
    if (expenses.length === 0) {
      return null;
    }

    return (
      <div className="summary-only">
        <div className="grand-total">
          <div className="grand-total-content">
            <h3>Kostenübersicht</h3>
            <div className="total-breakdown">
              {belegeExpenses.length > 0 && (
                <div className="breakdown-item">
                  <span className="category-name">Belege</span>
                  <span className="count-badge">{belegeExpenses.length}</span>
                  <span className="amount">
                    {formatCurrency(getTotalAmount(belegeExpenses))}
                  </span>
                </div>
              )}
              {spesenExpenses.length > 0 && (
                <div className="breakdown-item">
                  <span className="category-name">Spesenabrechnung</span>
                  <span className="count-badge">{spesenExpenses.length}</span>
                  <span className="amount">
                    {formatCurrency(getTotalAmount(spesenExpenses))}
                  </span>
                </div>
              )}
              <div className="breakdown-total">
                <strong>
                  Gesamtsumme: {formatCurrency(getTotalAmount(expenses))}
                </strong>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (expenses.length === 0) {
    const category =
      !showCategoryFilter && expenses.length === 0
        ? belegeExpenses.length === 0 && spesenExpenses.length === 0
          ? "Ausgaben"
          : ""
        : "Reisekosten";

    return (
      <div className="expense-list empty">
        <h3>{category}</h3>
        <p className="empty-message">
          Noch keine Ausgaben hinzugefügt. Fügen Sie Ihre erste Ausgabe hinzu!
        </p>
      </div>
    );
  }

  // If not showing category filter, determine which single category to show
  if (!showCategoryFilter) {
    const isBelegeCategory = expenses.some(
      (expense) => expense.category === ExpenseCategory.BELEGE,
    );
    const isSpesenCategory = expenses.some(
      (expense) => expense.category === ExpenseCategory.SPESENABRECHNUNG,
    );

    if (
      isBelegeCategory &&
      expenses.filter((e) => e.category === ExpenseCategory.BELEGE).length > 0
    ) {
      const categoryExpenses = expenses.filter(
        (e) => e.category === ExpenseCategory.BELEGE,
      );
      return (
        <div className="expense-lists-container single-category">
          <div className="expense-list">
            <div className="expense-list-header">
              <h3>
                <span className="header-title">Belege</span>
                <span className="count-badge">{categoryExpenses.length}</span>
              </h3>
              <div className="total-amount">
                Total: {formatCurrency(getTotalAmount(categoryExpenses))}
              </div>
            </div>

            <div
              className="table-scroll-container"
              ref={setScrollContainerRef(0)}
            >
              <div className="expense-table">
                <div className="table-header belege-header">
                  <div className="header-cell"></div>
                  <div className="header-cell">Beschreibung</div>
                  <div className="header-cell">Preis</div>
                  <div className="header-cell"></div>
                </div>

                <div className="table-body">
                  {categoryExpenses.map((expense) => (
                    <div key={expense.id} className="table-row belege-row">
                      <div className="table-cell receipt-cell" data-label="">
                        {expense.receipt ? (
                          <i
                            className="fas fa-paperclip"
                            title="Beleg angehängt"
                          ></i>
                        ) : (
                          <span className="no-receipt">-</span>
                        )}
                      </div>
                      <div
                        className="table-cell description-cell"
                        data-label="Beschreibung"
                      >
                        <div className="description-text">
                          {expense.description}
                        </div>
                      </div>
                      <div
                        className="table-cell amount-cell"
                        data-label="Preis"
                      >
                        {formatCurrency(expense.amount)}
                      </div>
                      <div className="table-cell actions-cell" data-label="">
                        <div className="action-buttons">
                          <button
                            onClick={() => handleEditExpense(expense)}
                            className="edit-btn"
                            title="Ausgabe bearbeiten"
                          >
                            <i className="fas fa-edit"></i>
                          </button>
                          <button
                            onClick={() => handleDeleteExpense(expense.id)}
                            className="delete-btn"
                            title="Ausgabe löschen"
                          >
                            <i className="fas fa-trash"></i>
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Edit Modal */}
          {editingExpense && (
            <EditExpenseModal
              expense={editingExpense}
              isOpen={!!editingExpense}
              onClose={handleCloseEditModal}
              onSave={handleSaveExpense}
            />
          )}
        </div>
      );
    }

    if (
      isSpesenCategory &&
      expenses.filter((e) => e.category === ExpenseCategory.SPESENABRECHNUNG)
        .length > 0
    ) {
      const categoryExpenses = expenses.filter(
        (e) => e.category === ExpenseCategory.SPESENABRECHNUNG,
      );
      return (
        <div className="expense-lists-container single-category">
          <div className="expense-list">
            <div className="expense-list-header">
              <h3>
                <span className="header-title">Spesenabrechnung</span>
                <span className="count-badge">{categoryExpenses.length}</span>
              </h3>
              <div className="total-amount">
                Total: {formatCurrency(getTotalAmount(categoryExpenses))}
              </div>
            </div>

            <div
              className="table-scroll-container spesen-table"
              ref={setScrollContainerRef(1)}
            >
              <div className="expense-table">
                <div className="table-header spesen-header">
                  <div className="header-cell"></div>
                  <div className="header-cell">Beschreibung</div>
                  <div className="header-cell">Datum</div>
                  <div className="header-cell">Ort</div>
                  <div className="header-cell">Dauer</div>
                  <div className="header-cell">Betrag</div>
                  <div className="header-cell"></div>
                </div>

                <div className="table-body">
                  {categoryExpenses.map((expense) => (
                    <div key={expense.id} className="table-row spesen-row">
                      <div className="table-cell receipt-cell" data-label="">
                        {expense.receipt ? (
                          <i
                            className="fas fa-paperclip"
                            title="Beleg angehängt"
                          ></i>
                        ) : (
                          <span className="no-receipt">-</span>
                        )}
                      </div>
                      <div
                        className="table-cell description-cell"
                        data-label="Beschreibung"
                      >
                        <div className="description-text">
                          {expense.description}
                        </div>
                      </div>
                      <div className="table-cell date-cell" data-label="Datum">
                        {expense.date ? formatDate(expense.date) : "-"}
                      </div>
                      <div
                        className="table-cell location-cell"
                        data-label="Ort"
                      >
                        {expense.location || "-"}
                      </div>
                      <div
                        className="table-cell duration-cell"
                        data-label="Dauer"
                      >
                        {expense.duration || "-"}
                      </div>
                      <div
                        className="table-cell amount-cell"
                        data-label="Betrag"
                      >
                        {formatCurrency(expense.amount)}
                      </div>
                      <div className="table-cell actions-cell" data-label="">
                        <div className="action-buttons">
                          <button
                            onClick={() => handleEditExpense(expense)}
                            className="edit-btn"
                            title="Ausgabe bearbeiten"
                          >
                            <i className="fas fa-edit"></i>
                          </button>
                          <button
                            onClick={() => handleDeleteExpense(expense.id)}
                            className="delete-btn"
                            title="Ausgabe löschen"
                          >
                            <i className="fas fa-trash"></i>
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Edit Modal */}
          {editingExpense && (
            <EditExpenseModal
              expense={editingExpense}
              isOpen={!!editingExpense}
              onClose={handleCloseEditModal}
              onSave={handleSaveExpense}
            />
          )}
        </div>
      );
    }

    // Return empty state for single category view
    return (
      <div className="expense-list empty">
        <h3>Ausgaben</h3>
        <p className="empty-message">
          Noch keine Ausgaben hinzugefügt. Fügen Sie Ihre erste Ausgabe hinzu!
        </p>
      </div>
    );
  }

  return (
    <div className="expense-lists-container">
      {/* Belege Table */}
      {belegeExpenses.length > 0 && (
        <div className="expense-list">
          <div className="expense-list-header">
            <h3>
              <span className="header-title">Belege</span>
              <span className="count-badge">{belegeExpenses.length}</span>
            </h3>
            <div className="total-amount">
              Total: {formatCurrency(getTotalAmount(belegeExpenses))}
            </div>
          </div>

          <div
            className="table-scroll-container"
            ref={setScrollContainerRef(2)}
          >
            <div className="expense-table">
              <div className="table-header belege-header">
                <div className="header-cell"></div>
                <div className="header-cell">Beschreibung</div>
                <div className="header-cell">Preis</div>
                <div className="header-cell"></div>
              </div>

              <div className="table-body">
                {belegeExpenses.map((expense) => (
                  <div key={expense.id} className="table-row belege-row">
                    <div className="table-cell receipt-cell" data-label="">
                      {expense.receipt ? (
                        <i
                          className="fas fa-paperclip"
                          title="Beleg angehängt"
                        ></i>
                      ) : (
                        <span className="no-receipt">-</span>
                      )}
                    </div>
                    <div
                      className="table-cell description-cell"
                      data-label="Beschreibung"
                    >
                      <div
                        className="description-text"
                        title={expense.description}
                      >
                        {expense.description}
                      </div>
                    </div>
                    <div className="table-cell amount-cell" data-label="Preis">
                      {formatCurrency(expense.amount)}
                    </div>
                    <div className="table-cell actions-cell" data-label="">
                      <div className="action-buttons">
                        <button
                          onClick={() => handleEditExpense(expense)}
                          className="edit-btn"
                          title="Ausgabe bearbeiten"
                        >
                          <i className="fas fa-edit"></i>
                        </button>
                        <button
                          onClick={() => handleDeleteExpense(expense.id)}
                          className="delete-btn"
                          title="Ausgabe löschen"
                        >
                          <i className="fas fa-trash"></i>
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="expense-summary">
            <div className="summary-item">
              <strong>Belege Gesamt: {belegeExpenses.length}</strong>
            </div>
            <div className="summary-item">
              <strong>
                Zwischensumme: {formatCurrency(getTotalAmount(belegeExpenses))}
              </strong>
            </div>
          </div>
        </div>
      )}

      {/* Spesenabrechnung Table */}
      {spesenExpenses.length > 0 && (
        <div className="expense-list">
          <div className="expense-list-header">
            <h3>
              <span className="header-title">Spesenabrechnung</span>
              <span className="count-badge">{spesenExpenses.length}</span>
            </h3>
            <div className="total-amount">
              Total: {formatCurrency(getTotalAmount(spesenExpenses))}
            </div>
          </div>

          <div
            className="table-scroll-container spesen-table"
            ref={setScrollContainerRef(3)}
          >
            <div className="expense-table">
              <div className="table-header spesen-header">
                <div className="header-cell"></div>
                <div className="header-cell">Beschreibung</div>
                <div className="header-cell">Datum</div>
                <div className="header-cell">Ort</div>
                <div className="header-cell">Dauer</div>
                <div className="header-cell">Betrag</div>
                <div className="header-cell"></div>
              </div>

              <div className="table-body">
                {spesenExpenses.map((expense) => (
                  <div key={expense.id} className="table-row spesen-row">
                    <div className="table-cell receipt-cell" data-label="">
                      {expense.receipt ? (
                        <i
                          className="fas fa-paperclip"
                          title="Beleg angehängt"
                        ></i>
                      ) : (
                        <span className="no-receipt">-</span>
                      )}
                    </div>
                    <div
                      className="table-cell description-cell"
                      data-label="Beschreibung"
                    >
                      <div
                        className="description-text"
                        title={expense.description}
                      >
                        {expense.description}
                      </div>
                    </div>
                    <div className="table-cell date-cell" data-label="Datum">
                      {expense.date ? formatDate(expense.date) : "-"}
                    </div>
                    <div className="table-cell location-cell" data-label="Ort">
                      {expense.location || "-"}
                    </div>
                    <div
                      className="table-cell duration-cell"
                      data-label="Dauer"
                    >
                      {expense.duration || "-"}
                    </div>
                    <div className="table-cell amount-cell" data-label="Betrag">
                      {formatCurrency(expense.amount)}
                    </div>
                    <div className="table-cell actions-cell" data-label="">
                      <div className="action-buttons">
                        <button
                          onClick={() => handleEditExpense(expense)}
                          className="edit-btn"
                          title="Ausgabe bearbeiten"
                        >
                          <i className="fas fa-edit"></i>
                        </button>
                        <button
                          onClick={() => handleDeleteExpense(expense.id)}
                          className="delete-btn"
                          title="Ausgabe löschen"
                        >
                          <i className="fas fa-trash"></i>
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="expense-summary">
            <div className="summary-item">
              <strong>Spesenabrechnung Gesamt: {spesenExpenses.length}</strong>
            </div>
            <div className="summary-item">
              <strong>
                Zwischensumme: {formatCurrency(getTotalAmount(spesenExpenses))}
              </strong>
            </div>
          </div>
        </div>
      )}

      {/* Grand Total if both categories have expenses */}
      {belegeExpenses.length > 0 && spesenExpenses.length > 0 && (
        <div className="grand-total">
          <div className="grand-total-content">
            <h3>Gesamtsumme</h3>
            <div className="total-breakdown">
              <div className="breakdown-item">
                <span className="category-name">Belege</span>
                <span className="amount">
                  {formatCurrency(getTotalAmount(belegeExpenses))}
                </span>
              </div>
              <div className="breakdown-item">
                <span className="category-name">Spesenabrechnung</span>
                <span className="amount">
                  {formatCurrency(getTotalAmount(spesenExpenses))}
                </span>
              </div>
              <div className="breakdown-total">
                <strong>
                  Gesamt: {formatCurrency(getTotalAmount(expenses))}
                </strong>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {editingExpense && (
        <EditExpenseModal
          expense={editingExpense}
          isOpen={!!editingExpense}
          onClose={handleCloseEditModal}
          onSave={handleSaveExpense}
        />
      )}
    </div>
  );
};

export default ExpenseList;
